import { Injectable } from '@angular/core';

export type AgeCategory = 'below60' | 'senior' | 'superSenior';
export type DisabilityLevel = 'none' | 'normal' | 'severe';

export const INCOME_TAX_CONSTRAINTS = {
  annualSalary:                  { min: 0, max: 100_000_000, step: 10_000 },
  interestIncome:                { min: 0, max: 10_000_000,  step: 1_000  },
  otherIncome:                   { min: 0, max: 10_000_000,  step: 1_000  },
  rentalIncome:                  { min: 0, max: 10_000_000,  step: 5_000  },
  homeLoanInterestLetOut:        { min: 0, max: 5_000_000,   step: 5_000  },
  hraExemption:                  { min: 0, max: 5_000_000,   step: 1_000  },
  section80C:                    { min: 0, max: 150_000,     step: 1_000  },
  section80CCD1B:                { min: 0, max: 50_000,      step: 1_000  },
  section80D:                    { min: 0, max: 100_000,     step: 1_000  },
  homeLoanInterestSelfOccupied:  { min: 0, max: 200_000,     step: 1_000  },
  section80E:                    { min: 0, max: 2_000_000,   step: 1_000  },
  section80G:                    { min: 0, max: 1_000_000,   step: 1_000  },
  rentPaid:                      { min: 0, max: 2_000_000,   step: 1_000  },
  section80EEA:                  { min: 0, max: 150_000,     step: 1_000  },
  section80EEB:                  { min: 0, max: 150_000,     step: 1_000  },
  section80DDB:                  { min: 0, max: 100_000,     step: 1_000  },
  professionalTax:               { min: 0, max: 2_500,       step: 100    },
  ltaExemption:                  { min: 0, max: 500_000,     step: 1_000  },
  section80CCD2:                 { min: 0, max: 500_000,     step: 1_000  }
} as const;

export interface IncomeTaxInputs {
  ageCategory:                    AgeCategory;
  annualSalary:                   number;
  interestIncome:                 number;
  otherIncome:                    number;
  rentalIncome:                   number;
  homeLoanInterestLetOut:         number;
  hraExemption:                   number;
  section80C:                     number;
  section80CCD1B:                 number;
  section80D:                     number;
  homeLoanInterestSelfOccupied:   number;
  section80E:                     number;
  section80G:                     number;
  rentPaid:                       number;
  section80EEA:                   number;
  section80EEB:                   number;
  section80DD:                    DisabilityLevel;
  section80U:                     DisabilityLevel;
  section80DDB:                   number;
  professionalTax:                number;
  ltaExemption:                   number;
  section80CCD2:                  number;
}

interface Slab { upto: number; rate: number; }

export interface SlabBreakup {
  rangeLabel: string;
  rate:       number;
  taxInSlab:  number;
}

export interface RegimeResult {
  grossTotalIncome:  number;
  standardDeduction: number;
  totalDeductions:   number;
  taxableIncome:      number;
  slabBreakup:        SlabBreakup[];
  taxBeforeRebate:    number;
  rebate:             number;
  taxAfterRebate:     number;
  cess:               number;
  totalTax:           number;
  takeHome:            number;
}

export interface IncomeTaxResult {
  newRegime:    RegimeResult;
  oldRegime:    RegimeResult;
  betterRegime: 'new' | 'old';
  savings:      number;
}

// FY 2025-26 (AY 2026-27) slabs
const NEW_REGIME_SLABS: Slab[] = [
  { upto: 400_000,   rate: 0    },
  { upto: 800_000,   rate: 0.05 },
  { upto: 1_200_000, rate: 0.10 },
  { upto: 1_600_000, rate: 0.15 },
  { upto: 2_000_000, rate: 0.20 },
  { upto: 2_400_000, rate: 0.25 },
  { upto: Infinity,  rate: 0.30 }
];

function oldRegimeSlabs(age: AgeCategory): Slab[] {
  if (age === 'senior') {
    return [
      { upto: 300_000,   rate: 0    },
      { upto: 500_000,   rate: 0.05 },
      { upto: 1_000_000, rate: 0.20 },
      { upto: Infinity,  rate: 0.30 }
    ];
  }
  if (age === 'superSenior') {
    return [
      { upto: 500_000,   rate: 0    },
      { upto: 1_000_000, rate: 0.20 },
      { upto: Infinity,  rate: 0.30 }
    ];
  }
  return [
    { upto: 250_000,   rate: 0    },
    { upto: 500_000,   rate: 0.05 },
    { upto: 1_000_000, rate: 0.20 },
    { upto: Infinity,  rate: 0.30 }
  ];
}

const NEW_REGIME_STANDARD_DEDUCTION = 75_000;
const OLD_REGIME_STANDARD_DEDUCTION = 50_000;
const NEW_REGIME_REBATE_THRESHOLD   = 1_200_000;
const OLD_REGIME_REBATE_THRESHOLD   = 500_000;
const HOUSE_PROPERTY_LOSS_CAP       = 200_000;
const CESS_RATE = 0.04;

function section80DCap(age: AgeCategory): number {
  return age === 'below60' ? 25_000 : 50_000;
}

function interestDeductionCap(age: AgeCategory): number {
  // Section 80TTA (below 60) vs 80TTB (senior/super senior), old regime only.
  return age === 'below60' ? 10_000 : 50_000;
}

function section80DDBCap(age: AgeCategory): number {
  return age === 'below60' ? 40_000 : 100_000;
}

function disabilityDeduction(level: DisabilityLevel): number {
  if (level === 'severe') return 125_000;
  if (level === 'normal') return 75_000;
  return 0;
}

function section80GGDeduction(rentPaid: number, hraExemption: number, adjustedTotalIncome: number): number {
  if (hraExemption > 0 || rentPaid <= 0) return 0;
  const excessRent = Math.max(0, rentPaid - adjustedTotalIncome * 0.10);
  return Math.max(0, Math.min(60_000, adjustedTotalIncome * 0.25, excessRent));
}

function slabTaxWithBreakup(taxableIncome: number, slabs: Slab[]): { tax: number; breakup: SlabBreakup[] } {
  let tax = 0;
  let prevUpto = 0;
  const breakup: SlabBreakup[] = [];
  for (const slab of slabs) {
    if (taxableIncome <= prevUpto) break;
    const taxableInSlab = Math.min(taxableIncome, slab.upto) - prevUpto;
    const taxInSlab = taxableInSlab * slab.rate;
    tax += taxInSlab;
    const rangeLabel = slab.upto === Infinity
      ? `Above ₹${prevUpto.toLocaleString('en-IN')}`
      : `₹${prevUpto.toLocaleString('en-IN')} – ₹${slab.upto.toLocaleString('en-IN')}`;
    breakup.push({ rangeLabel, rate: slab.rate, taxInSlab });
    prevUpto = slab.upto;
  }
  return { tax, breakup };
}

@Injectable({ providedIn: 'root' })
export class IncomeTaxCalculatorService {

  private computeRegime(
    grossTotalIncome: number, standardDeduction: number, otherDeductions: number,
    slabs: Slab[], rebateThreshold: number
  ): RegimeResult {
    const taxableIncome = Math.max(0, grossTotalIncome - standardDeduction - otherDeductions);
    const { tax: taxBeforeRebate, breakup: slabBreakup } = slabTaxWithBreakup(taxableIncome, slabs);
    const rebate = taxableIncome <= rebateThreshold ? taxBeforeRebate : 0;
    const taxAfterRebate = taxBeforeRebate - rebate;
    const cess = taxAfterRebate * CESS_RATE;
    const totalTax = taxAfterRebate + cess;

    return {
      grossTotalIncome, standardDeduction, totalDeductions: otherDeductions,
      taxableIncome, slabBreakup, taxBeforeRebate, rebate, taxAfterRebate, cess, totalTax,
      takeHome: grossTotalIncome - totalTax
    };
  }

  calculate(inputs: IncomeTaxInputs): IncomeTaxResult {
    const netHouseProperty = inputs.rentalIncome * 0.7 - inputs.homeLoanInterestLetOut;
    const housePropertyOld = Math.max(netHouseProperty, -HOUSE_PROPERTY_LOSS_CAP);
    const housePropertyNew = Math.max(netHouseProperty, 0);

    const baseIncome = inputs.annualSalary + inputs.interestIncome + inputs.otherIncome;

    const interestDeduction = Math.min(inputs.interestIncome, interestDeductionCap(inputs.ageCategory));
    const section80GG = section80GGDeduction(inputs.rentPaid, inputs.hraExemption, baseIncome + housePropertyOld);
    const section80CCD2Old = Math.min(inputs.section80CCD2, inputs.annualSalary * 0.10);
    const section80CCD2New = Math.min(inputs.section80CCD2, inputs.annualSalary * 0.14);

    const oldDeductionsTotal =
      inputs.hraExemption +
      section80GG +
      Math.min(inputs.section80C, INCOME_TAX_CONSTRAINTS.section80C.max) +
      Math.min(inputs.section80CCD1B, INCOME_TAX_CONSTRAINTS.section80CCD1B.max) +
      Math.min(inputs.section80D, section80DCap(inputs.ageCategory)) +
      Math.min(inputs.homeLoanInterestSelfOccupied, INCOME_TAX_CONSTRAINTS.homeLoanInterestSelfOccupied.max) +
      Math.min(inputs.section80EEA, INCOME_TAX_CONSTRAINTS.section80EEA.max) +
      Math.min(inputs.section80EEB, INCOME_TAX_CONSTRAINTS.section80EEB.max) +
      disabilityDeduction(inputs.section80DD) +
      disabilityDeduction(inputs.section80U) +
      Math.min(inputs.section80DDB, section80DDBCap(inputs.ageCategory)) +
      Math.min(inputs.professionalTax, INCOME_TAX_CONSTRAINTS.professionalTax.max) +
      inputs.ltaExemption +
      inputs.section80E +
      inputs.section80G +
      interestDeduction +
      section80CCD2Old;

    const newRegime = this.computeRegime(
      baseIncome + housePropertyNew, NEW_REGIME_STANDARD_DEDUCTION, section80CCD2New,
      NEW_REGIME_SLABS, NEW_REGIME_REBATE_THRESHOLD
    );
    const oldRegime = this.computeRegime(
      baseIncome + housePropertyOld, OLD_REGIME_STANDARD_DEDUCTION, oldDeductionsTotal,
      oldRegimeSlabs(inputs.ageCategory), OLD_REGIME_REBATE_THRESHOLD
    );

    const betterRegime = newRegime.totalTax <= oldRegime.totalTax ? 'new' : 'old';
    const savings = Math.abs(newRegime.totalTax - oldRegime.totalTax);

    return { newRegime, oldRegime, betterRegime, savings };
  }
}
